import { inject, Injectable } from '@angular/core';
import {
    collection,
    collectionData,
    doc,
    Firestore,
    query,
    runTransaction,
    where,
    updateDoc,
    getDoc,
    Transaction
} from '@angular/fire/firestore';
import {
    Collections,
    DEFAULT_PRODUCTION_ENVIRONMENT,
    DashboardProject,
    DEFAULT_GROUP,
    Environment,
    EnvironmentByProject,
    Flag,
    Label,
    LabelByProject,
    Project,
    ProjectNameIcon,
    Role,
    DEFAULT_DEV_ENVIRONMENT
} from '@ffx/common';
import { combineLatest, from, map, Observable, of, switchMap, take } from 'rxjs';
import { DbProject, Group } from '../../interfaces/db-interfaces';
import { v4 as uuidv4 } from 'uuid';
import { replaceLabel } from '../../utils/label.functions';

@Injectable({
    providedIn: 'root'
})
export class ProjectService {
    private fireStore = inject(Firestore);

    addGroup(project: DbProject, group: Group, environments: Environment[], flags: Flag[]): Observable<string[]> {
        return from(
            runTransaction(this.fireStore, async (transaction) => {
                //Update groups in project
                transaction.update(doc(this.fireStore, `${Collections.projects}/${project.id}`), {
                    groups: project.groups ? project.groups.concat([group]) : [group]
                });

                const environmentIds: string[] = [];

                //Add environments for that group
                environments.forEach((environment) => {
                    const environmentId = uuidv4();
                    environmentIds.push(environmentId);
                    transaction.set(doc(this.fireStore, `${Collections.environment}/${environmentId}`), {
                        groupId: group.id,
                        name: environment.name,
                        cssColour: environment.cssColour,
                        isProd: environment.name === DEFAULT_PRODUCTION_ENVIRONMENT.name ? true : false
                    });

                    //Add flags for each environment
                    flags.forEach((flag) => {
                        transaction.set(doc(this.fireStore, `${Collections.featureFlags}/${uuidv4()}`), {
                            key: flag.key,
                            name: flag.name,
                            description: flag.description,
                            labels: flag.labels,
                            isOn: false,
                            environmentId: environmentId
                        });
                    });
                });
                return environmentIds;
            })
        );
    }

    deleteGroup(project: DbProject, group: Group): Observable<void> {
        const environmentsCollection = collection(this.fireStore, Collections.environment);
        const environmentsQuery = query(environmentsCollection, where('groupId', '==', group.id));
        const environmentCollectionData = collectionData(environmentsQuery, { idField: 'id' });

        return environmentCollectionData.pipe(
            take(1),
            switchMap((environments) => {
                const environmentIds = environments.map((environment) => environment['id']);
                const flagsCollection = collection(this.fireStore, Collections.featureFlags);
                const flagsQuery = query(flagsCollection, where('environmentId', 'in', environmentIds));
                return collectionData(flagsQuery, { idField: 'id' }).pipe(
                    take(1),
                    switchMap((flags) => {
                        return from(
                            runTransaction(this.fireStore, async (transaction) => {
                                //Update groups in project
                                const filteredGroups = project.groups?.filter(
                                    (currentGroup) => currentGroup.name !== group.name
                                );
                                transaction.update(doc(this.fireStore, `${Collections.projects}/${project.id}`), {
                                    groups: filteredGroups
                                });

                                //delete flags
                                flags.forEach((flag) => {
                                    transaction.delete(
                                        doc(this.fireStore, `${Collections.featureFlags}/${flag['id']}`)
                                    );
                                });

                                //delete environments
                                environmentIds.forEach((environmentId) => {
                                    transaction.delete(
                                        doc(this.fireStore, `${Collections.environment}/${environmentId}`)
                                    );
                                });
                            })
                        );
                    })
                );
            })
        );
    }

    updateGroup(project: DbProject, groups: Group[]): Observable<void> {
        return from(
            updateDoc(doc(this.fireStore, `${Collections.projects}/${project.id}`), {
                groups: groups
            })
        );
    }

    getEnvironmentsByGroupId(groupId: string): Observable<any> {
        const environmentsCollection = collection(this.fireStore, Collections.environment);
        const environmentQuery = query(environmentsCollection, where('groupId', '==', groupId));
        return collectionData(environmentQuery, { idField: 'id' });
    }

    getEnvironmentsByName(name: string): Observable<any> {
        const environmentCollection = collection(this.fireStore, Collections.environment);
        const environmentQuery = query(environmentCollection, where('name', '==', name));
        return collectionData(environmentQuery, { idField: 'id' });
    }

    getProject(projectId: string): Observable<any> {
        const projectDocRef = doc(this.fireStore, `${Collections.projects}/${projectId}`);
        return from(getDoc(projectDocRef));
    }

    getDefaultProductionEnvironmentId(projectId: string): Observable<any> {
        const environmentsCollection = collection(this.fireStore, Collections.environment);
        const environmentQuery = query(
            environmentsCollection,
            where('groupId', '==', projectId),
            where('isProd', '==', true)
        );
        return collectionData(environmentQuery, { idField: 'id' });
    }

    addEnvironmentToGroup(
        environmentToAdd: EnvironmentByProject,
        prodFeatureFlags: Flag[],
        groupId: string
    ): Observable<void> {
        return from(
            //Add environments for that group
            runTransaction(this.fireStore, async (transaction) => {
                transaction.set(doc(this.fireStore, `${Collections.environment}/${environmentToAdd.id}`), {
                    name: environmentToAdd.name,
                    cssColour: environmentToAdd.cssColour,
                    groupId: groupId
                });
                if (prodFeatureFlags) {
                    prodFeatureFlags.forEach((flag) => {
                        transaction.set(doc(this.fireStore, `${Collections.featureFlags}/${uuidv4()}`), {
                            key: flag.key,
                            name: flag.name,
                            description: flag.description,
                            labels: flag.labels,
                            isOn: false,
                            environmentId: environmentToAdd.id
                        });
                    });
                }
            })
        );
    }

    deleteEnvironment(allEnvironmentsToDelete: Environment[]): Observable<void> {
        const featureFlagsCollection = collection(this.fireStore, Collections.featureFlags);
        const featureFlagsQuery = query(
            featureFlagsCollection,
            where(
                'environmentId',
                'in',
                allEnvironmentsToDelete.map((environment) => environment.id)
            )
        );
        return collectionData(featureFlagsQuery, { idField: 'id' }).pipe(
            take(1),
            switchMap((flags) => {
                return from(
                    runTransaction(this.fireStore, async (transaction) => {
                        flags.forEach((flag: any) =>
                            transaction.delete(doc(this.fireStore, `${Collections.featureFlags}/${flag['id']}`))
                        );
                        allEnvironmentsToDelete.forEach((environmentToDelete) => {
                            transaction.delete(
                                doc(this.fireStore, `${Collections.environment}/${environmentToDelete.id}`)
                            );
                        });
                    })
                );
            })
        );
    }

    deleteLabel(labelToDelete: LabelByProject, newLabels: Label[]): Observable<void> {
        const featureFlagsCollection = collection(this.fireStore, Collections.featureFlags);
        const featureFlagsQuery = query(
            featureFlagsCollection,
            where('labels', 'array-contains', {
                name: labelToDelete.name,
                icon: labelToDelete.icon
            })
        );
        return collectionData(featureFlagsQuery, { idField: 'id' }).pipe(
            take(1),
            switchMap((flags: any) => {
                return from(
                    runTransaction(this.fireStore, async (transaction) => {
                        flags.forEach((flag: any) =>
                            transaction.update(doc(this.fireStore, `${Collections.featureFlags}/${flag['id']}`), {
                                labels: flag['labels'].filter((label: any) => label.name !== labelToDelete.name)
                            })
                        );
                        transaction.update(doc(this.fireStore, `${Collections.projects}/${labelToDelete.projectId}`), {
                            labels: newLabels
                        });
                    })
                );
            })
        );
    }

    getDashboardProjectsByUserId(userUid: string): Observable<DashboardProject[]> {
        const projectsRoleUserCollection = collection(this.fireStore, Collections.projectRoleUser);
        const getProjectsByUserIdQuery = query(projectsRoleUserCollection, where('userUid', '==', userUid));

        const rolesCollection = collection(this.fireStore, Collections.roles);
        const getRolesQuery = query(rolesCollection);

        return combineLatest({
            projectIdsByUser: collectionData(getProjectsByUserIdQuery, { idField: 'id' }).pipe(take(1)),
            roles: collectionData(getRolesQuery, { idField: 'id' }).pipe(take(1))
        }).pipe(
            switchMap(({ projectIdsByUser, roles }) => {
                if (projectIdsByUser.length) {
                    const projectsCollection = collection(this.fireStore, Collections.projects);
                    const getAllUsersProjects = query(
                        projectsCollection,
                        where(
                            '__name__',
                            'in',
                            projectIdsByUser.map((projectRoleUser) => projectRoleUser['projectId'])
                        )
                    );
                    return collectionData(getAllUsersProjects, { idField: 'id' }).pipe(
                        map((projects) => {
                            const ownerId = roles.find((role: any) => role['name'] == 'Owner')?.id;
                            return projectIdsByUser.map((projectRoleUser) => {
                                const userRole: Role = projectRoleUser['roleId'] === ownerId ? 'Owner' : 'Member';
                                const project = projects.find((project) => project.id === projectRoleUser['projectId']);
                                return {
                                    icon: project ? project['icon'] : '',
                                    name: project ? project['name'] : '',
                                    id: project ? project['id'] : '',
                                    role: userRole
                                };
                            });
                        })
                    );
                } else {
                    return of([]);
                }
            })
        );
    }

    addProject(project: Project, userUid: string, projectDocId: string): Observable<string[]> {
        const projectRoleUserDocId = uuidv4();
        const projectsDocRef = doc(this.fireStore, `${Collections.projects}/${projectDocId}`);
        const projectsRoleUserDocRef = doc(this.fireStore, `${Collections.projectRoleUser}/${projectRoleUserDocId}`);
        const rolesCollection = collection(this.fireStore, Collections.roles);
        const rolesQuery = query(rolesCollection, where('name', '==', 'Owner'));

        const defaultGroup: Group = { ...DEFAULT_GROUP, id: projectDocId };
        return collectionData(rolesQuery, { idField: 'id' }).pipe(
            take(1),
            switchMap((roles: any[]) => {
                return from(
                    runTransaction(this.fireStore, async (transaction) => {
                        transaction.set(projectsDocRef, {
                            name: project.name,
                            labels: project.labels,
                            icon: project.icon,
                            groups: [defaultGroup]
                        });
                        transaction.set(projectsRoleUserDocRef, {
                            projectId: projectDocId,
                            roleId: roles[0].id,
                            userUid
                        });
                        const devEnvironmentId = uuidv4();
                        transaction.set(doc(this.fireStore, `${Collections.environment}/${devEnvironmentId}`), {
                            groupId: defaultGroup.id,
                            projectId: projectDocId,
                            name: DEFAULT_DEV_ENVIRONMENT.name,
                            cssColour: DEFAULT_DEV_ENVIRONMENT.cssColour
                        });
                        const prodEnvironmentId = uuidv4();
                        transaction.set(doc(this.fireStore, `${Collections.environment}/${prodEnvironmentId}`), {
                            groupId: defaultGroup.id,
                            projectId: projectDocId,
                            name: DEFAULT_PRODUCTION_ENVIRONMENT.name,
                            cssColour: DEFAULT_PRODUCTION_ENVIRONMENT.cssColour,
                            isProd: true
                        });
                        return [devEnvironmentId, prodEnvironmentId];
                    })
                );
            })
        );
    }

    addLabel(projectId: string, newLabels: Label[]): Observable<void> {
        return from(
            updateDoc(doc(this.fireStore, `${Collections.projects}/${projectId}`), {
                labels: newLabels
            })
        );
    }

    updateEnvironment(environmentToUpdate: Environment): Observable<void> {
        const environmentRef = doc(this.fireStore, `${Collections.environment}/${environmentToUpdate.id}`);

        return from(
            updateDoc(environmentRef, {
                name: environmentToUpdate.name,
                cssColour: environmentToUpdate.cssColour
            })
        );
    }

    updateLabel(projectId: string, labelToReplace: Label, newLabel: Label, newLabels: Label[]): Observable<void> {
        const featureFlagsCollection = collection(this.fireStore, Collections.featureFlags);
        const featureFlagsQuery = query(featureFlagsCollection, where('labels', 'array-contains', labelToReplace));
        return collectionData(featureFlagsQuery, { idField: 'id' }).pipe(
            take(1),
            switchMap((featureFlags: any) => {
                return from(
                    runTransaction(this.fireStore, async (transaction) => {
                        featureFlags.forEach((flag: any) =>
                            transaction.update(doc(this.fireStore, `${Collections.featureFlags}/${flag['id']}`), {
                                labels: replaceLabel(flag['labels'], labelToReplace, newLabel)
                            })
                        );
                        transaction.update(doc(this.fireStore, `${Collections.projects}/${projectId}`), {
                            labels: newLabels
                        });
                    })
                );
            })
        );
    }

    updateProjectNameIcon(updatedProject: ProjectNameIcon): Observable<void> {
        return from(
            updateDoc(doc(this.fireStore, `${Collections.projects}/${updatedProject.projectId}`), {
                name: updatedProject.name,
                icon: updatedProject.icon
            })
        );
    }

    deleteProject(projectId: string): Observable<void> {
        const featureFlagsCollection = collection(this.fireStore, Collections.featureFlags);
        const featureFlagsQuery = query(featureFlagsCollection);
        const environmentsCollection = collection(this.fireStore, Collections.environment);
        const environmentsQuery = query(environmentsCollection, where('projectId', '==', projectId));
        const projectRoleUserCollection = collection(this.fireStore, Collections.projectRoleUser);
        const projectRoleUserQuery = query(projectRoleUserCollection, where('projectId', '==', projectId));
        return combineLatest({
            projectRoleUsers: collectionData(projectRoleUserQuery, { idField: 'id' }).pipe(take(1)),
            environments: collectionData(environmentsQuery, { idField: 'id' }).pipe(take(1)),
            featureFlags: collectionData(featureFlagsQuery, { idField: 'id' }).pipe(take(1))
        }).pipe(
            switchMap(({ projectRoleUsers, environments, featureFlags }) => {
                return from(
                    runTransaction(this.fireStore, async (transaction: Transaction) => {
                        const environmentIds = environments.map((environment) => environment['id']);
                        featureFlags.forEach((flag) => {
                            if (environmentIds.includes(flag['environmentId'])) {
                                transaction.delete(doc(this.fireStore, `${Collections.featureFlags}/${flag['id']}`));
                            }
                        });
                        environmentIds.forEach((environmentId) => {
                            transaction.delete(doc(this.fireStore, `${Collections.environment}/${environmentId}`));
                        });
                        projectRoleUsers.forEach((projectRoleUser) => {
                            transaction.delete(
                                doc(this.fireStore, `${Collections.projectRoleUser}/${projectRoleUser['id']}`)
                            );
                        });
                        transaction.delete(doc(this.fireStore, `${Collections.projects}/${projectId}`));
                    })
                );
            })
        );
    }
}
